import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { useTranslation } from './LanguageContext';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { syncLanguage } = useTranslation();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser && savedUser !== 'undefined') {
      try {
        const parsed = JSON.parse(savedUser);
        setUser(parsed);
        // Apply saved user's language immediately on session restore
        if (parsed?.language) syncLanguage(parsed.language);
      } catch (e) {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        setLoading(false);
        return;
      }
      // Verify token is still valid and get fresh user data
      authAPI.getMe()
        .then(res => {
          setUser(res.data.user);
          localStorage.setItem('user', JSON.stringify(res.data.user));
          // Apply fresh language from server profile
          if (res.data.user?.language) syncLanguage(res.data.user.language);
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password, captchaToken) => {
    const res = await authAPI.login({ email, password, captchaToken });
    localStorage.setItem('token', res.data.token);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    // Apply this user's language immediately on login
    if (res.data.user?.language) syncLanguage(res.data.user.language);
    // Surface any guest stories claimed on login
    if (res.data.claimedStories > 0) {
      localStorage.setItem('claimedStories', String(res.data.claimedStories));
    }
    return res.data;
  };

  const register = async (displayName, email, password, captchaToken) => {
    const res = await authAPI.register({ displayName, email, password, captchaToken });
    localStorage.setItem('token', res.data.token);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    // New users default to 'en', reset language
    syncLanguage(res.data.user?.language || 'en');
    // Surface any guest stories claimed on registration
    if (res.data.claimedStories > 0) {
      localStorage.setItem('claimedStories', String(res.data.claimedStories));
    }
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Reset language to English on logout so next user starts fresh
    localStorage.removeItem('language');
    setUser(null);
  };

  const value = {
    user,
    setUser,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};